<script lang="ts" setup>
	import type { ModalOption } from '@/components/Dialogs/options.vue'
	import usePurchaseApi from '~/services/apis/purchases-info'

	const props = defineProps<{
		show: boolean
		onlyHealth?: boolean
		checkForSlots?: boolean
		policyInfo?: {
			customer_id: string
			first_name: string
			last_name: string
			phone_number: string
		}
	}>()

	const emit = defineEmits<{
		(arg: 'update:show', param: boolean): void
		(arg: 'proceed', param: PlanInformationContext): void
		(arg: 'getFormRecord', param: object): void
	}>()
	const { allPurchases, getPurchaseDetail } = usePurchaseApi()
	const { formatDate } = useCustomDate()
	const isLoading = shallowRef(false)
	const fetchingPlan = shallowRef(false)
	const selectSlotDialog = shallowRef(false)
	const currentPage = shallowRef(1)
	const totalPages = shallowRef(0)
	const selectedPurchase = ref<PlanInformationContext | null>(null)

	interface PlanModalOption extends ModalOption<string> {
		slot_left: number
		purchase_date: string
		cover_period: string | number
		is_expired?: boolean
		isHealth?: boolean
	}

	const availablePlans = ref<PlanModalOption[]>([])
	const { addProductsIconFill, productCategoryType } = useUtils()

	const getPlans = async () => {
		if (isLoading.value) return
		isLoading.value = true
		try {
			const response = await allPurchases<PlanInformationContext[]>({
				page: currentPage.value,
				customer_type: 'business'
			})
			if (response) {
				const data = response
				totalPages.value = Number(data.total_pages || 0)
				currentPage.value++
				if (data.items?.length) {
					const { toLower } = useUtils()
					// Filter Out to make sure no duplicates is found
					const newItems: PlanModalOption[] = data.items
						.filter(item => !availablePlans.value.find(existingItem => existingItem.id === item.id))
						.map(item => {
							const prodCategory = addProductsIconFill(
								item.product?.productCategory?.name,
								item.product.name
							)
							return {
								key: item.id,
								id: item.id,
								slot_left: Number(item.remaining_staff_slot || 0),
								name: item.product?.name || 'N/A',
								desc: `You currently have ${item?.remaining_staff_slot || 0} slot left`,
								icon: {
									dir: prodCategory.dir,
									name: prodCategory.name,
									pathClass: prodCategory.pathClass
								},
								purchase_date: formatDate(item.purchase_date, 'DD/MM/YYYY'),
								cover_period: item?.meta?.cover_period ?? '',
								is_expired: item?.is_expired,
								isHealth: Boolean(
									toLower(item?.product?.productCategory?.name) === toLower(ProductCategory.Health)
								)
							}
						})

					// Filter  Products
					const plans = newItems.filter(item => item.slot_left > 0 || item.isHealth)
					availablePlans.value = [...availablePlans.value, ...plans]
				}
			}
		} finally {
			isLoading.value = false
		}
	}

	const trackScroll = (e?: UIEvent) => {
		// Fetch new plans from pagination
		const dialogElement = useElementSelector('#dialog-body')
		if (dialogElement.value && e) {
			const elTarget = e.target as HTMLElement

			if (Math.round(elTarget.clientHeight + elTarget.scrollTop) >= dialogElement.value.scrollHeight) {
				fetchingPlan.value = true
				getPlans().finally(() => {
					fetchingPlan.value = false
				})
			}
		}
	}

	const getSelectedPlan = async (arg: ModalOption<string>) => {
		const planArg = arg as PlanModalOption
		if (planArg?.is_expired) {
			return useNotification().alert({
				status: 400,
				message: 'You can not add a staff to an expired plan for now.'
			})
		}
		if (arg.id) {
			try {
				fetchingPlan.value = true
				const result = await getPurchaseDetail<PlanInformationContext>(arg.id as string)
				if (result?.data) {
					selectedPurchase.value = result.data
					const category = productCategoryType(
						selectedPurchase.value.product?.productCategory?.name ?? '',
						selectedPurchase.value.product?.code_name
					)

					if (!props.checkForSlots || (!category?.isAuto && !category?.isGadget)) {
						emit('proceed', { ...result.data, id: arg.id as string })
					} else {
						selectSlotDialog.value = true
					}
					emit('update:show', false)
				}
			} finally {
				fetchingPlan.value = false
			}
		}
	}

	onUnmounted(() => {
		window.removeEventListener('scroll', () => trackScroll())
	})

	watch(
		() => props.show,
		async active => {
			if (active) {
				await getPlans()
			}
		}
	)
</script>

<template>
	<div>
		<dialogs-options
			:show="show"
			title="Select Plan"
			paragraph="Select an active plan to upload your staff"
			body-class="max-h-[400px] overflow-y-auto"
			:category-options="availablePlans"
			:loading="isLoading"
			:processing="fetchingPlan"
			:ui="{ modal: 'pb-5 px-0' }"
			@on-track-scroll="trackScroll"
			@proceed="getSelectedPlan"
			@cancel="emit('update:show', false)"
		>
			<template #sup="{ option }">
				<div
					class="inline-flex items-center gap-x-1 rounded-md border border-gray-200 bg-white px-1.5 text-[10px] text-gray-500"
				>
					Purchased:
					<span class="text-purple-500">
						{{ option?.purchase_date }}
					</span>
				</div>
			</template>
			<template #empty>
				<shared-empty-state title-class="" title="No available plan with a pending slot" icon-position="top" />
			</template>
		</dialogs-options>
		<dialogs-select-slot-list
			v-model="selectSlotDialog"
			:policy-info="policyInfo"
			:customer-id="policyInfo?.customer_id ?? ''"
			:category-type="selectedPurchase?.product?.productCategory?.name ?? ''"
			:purchase-id="String(selectedPurchase?.id ?? '')"
			:product-code="Number(selectedPurchase?.product?.code_name ?? 0)"
			@close=";[emit('update:show', true), (selectSlotDialog = false)]"
			@submit-form="$emit('getFormRecord', $event)"
		/>
	</div>
</template>
